import type { SearchHit } from '@contracts';
import { ArrowUpRight, CircleSlash, Sparkles, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useMissingSources } from '../lib/sources';
import { absolute, ago, DAY, freshness } from '../lib/time';
import { useScope } from '../scope/scope';
import { buttonClass } from '../ui/Button';
import { cn } from '../ui/cn';
import { CopyButton, TimeAgo, useNow } from '../ui/misc';
import { osLabel, StatusBadge } from '../ui/status';
import { recordPath } from './paths';
import { DeviceIdentity } from '../devices/DeviceIdentity';

/** What the record says, and how far to trust it, in one or two sentences. */
export function deviceSummary(hit: SearchHit, now: number): { text: string; tone: 'neutral' | 'warn' } {
  const os = osLabel(hit.os);
  const where = [hit.organizationName, hit.siteName].filter(Boolean).join(' · ');
  const lead = `${os ? `${os} device` : 'Device'}${where ? ` at ${where}` : ''}.`;
  if (!hit.lastSeenAt) return { tone: 'warn', text: `${lead} It has never checked in, so its state is unknown — not necessarily offline. It may be newly enrolled.` };
  const age = now - Date.parse(hit.lastSeenAt);
  const seen = `last checked in ${ago(hit.lastSeenAt, now)}`;
  if (hit.managed) {
    // The agent reports continuously: speak from its open signals.
    if (hit.status === 'offline') {
      // OS support is read against its published end date at the moment of reading (WRMM-157); the rest is the last report.
      const n = hit.signals.length, lifecycle = hit.signals.some(s => s.key === 'os.unsupported' || s.key === 'os.ending');
      const issues = !n ? '' : lifecycle ? ` It has ${n} open issue${n === 1 ? '' : 's'}: OS support as of now, anything else as of then.`
        : ` It had ${n} open issue${n === 1 ? '' : 's'} then.`;
      return { tone: 'warn', text: `${lead} The agent ${seen} and has gone quiet, so anything below describes that moment, not now${lifecycle
        ? ', except OS support, which follows its published end date' : ''}.${issues}` };
    }
    if (!hit.signals.length) return { tone: 'neutral', text: `${lead} The agent is reporting normally (${seen}) and nothing needs attention.` };
    const top = hit.signals[0]!;
    return { tone: top.severity === 'info' ? 'neutral' : 'warn',
      text: `${lead} ${top.title}${hit.signals.length > 1 ? `, plus ${hit.signals.length - 1} more issue${hit.signals.length === 2 ? '' : 's'}` : ''}. The agent ${seen}.` };
  }
  switch (hit.status) {
    case 'healthy': return age > 7 * DAY
      ? { tone: 'warn', text: `${lead} It reports Healthy, but ${seen}, so that status may be out of date.` }
      : { tone: 'neutral', text: `${lead} It reports Healthy and ${seen}.` };
    case 'attention': return { tone: 'warn', text: `${lead} It reported a condition that needs attention and ${seen}.` };
    case 'offline': return { tone: 'warn', text: `${lead} It is marked Offline; it ${seen}.` };
    default: return { tone: 'warn', text: `${lead} No status has been reported; it ${seen}.` };
  }
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[120px_1fr] items-baseline gap-3 py-2">
      <dt className="text-[13px] text-ink-3">{label}</dt>
      <dd className="min-w-0 text-ink">{children}</dd>
    </div>
  );
}

export function DeviceFacts({ hit }: { hit: SearchHit }) {
  const { mspId } = useScope();
  const f = freshness(hit.lastSeenAt);
  return (
    <dl className="divide-y divide-line/70">
      <Fact label="Status"><StatusBadge status={hit.status} /></Fact>
      <Fact label="Last check-in">
        {hit.lastSeenAt ? (
          <span className="flex flex-wrap items-baseline gap-x-2">
            <TimeAgo value={hit.lastSeenAt} />
            <span className="text-[12.5px] text-ink-3">{absolute(hit.lastSeenAt)}</span>
          </span>
        ) : <span className="text-ink-3">Never recorded</span>}
        {(f === 'stale' || f === 'quiet') && <p className="mt-0.5 text-[12.5px] text-ink-3">Treat the status above as {f === 'stale' ? 'possibly out of date' : 'a few days old'}.</p>}
      </Fact>
      <Fact label="Operating system">{osLabel(hit.os) ?? <span className="italic text-ink-3">Not reported</span>}</Fact>
      <Fact label="Serial">
        {hit.serial ? <span className="flex items-center gap-1"><span className="truncate font-mono text-[13px]">{hit.serial}</span><CopyButton value={hit.serial} label="Copy serial" /></span>
          : <span className="italic text-ink-3">Not reported</span>}
      </Fact>
      <Fact label="Customer">
        {hit.organizationName ? <Link className="font-medium hover:underline" to={recordPath(mspId, 'organization', hit.organizationId)}>{hit.organizationName}</Link> : <span className="text-ink-3">Unavailable</span>}
      </Fact>
      <Fact label="Site">
        {hit.siteId && hit.siteName ? <Link className="font-medium hover:underline" to={recordPath(mspId, 'site', hit.siteId)}>{hit.siteName}</Link> : <span className="text-ink-3">Unavailable</span>}
      </Fact>
      <Fact label="Record updated"><span className="text-ink-2" title={absolute(hit.source.updatedAt)}>{ago(hit.source.updatedAt)}</span></Fact>
      <Fact label="Device ID"><DeviceIdentity device={hit} /></Fact>
      <Fact label="Record ID"><span className="flex items-center gap-1"><span className="truncate font-mono text-[12px] text-ink-2">{hit.id}</span><CopyButton value={hit.id} label="Copy record ID" /></span></Fact>
    </dl>
  );
}

/** Data this console cannot see yet, so a quiet record is not read as full coverage. */
export function NotVisible({ compact = false }: { compact?: boolean }) {
  const missingSources = useMissingSources();
  return (
    <div className={cn('rounded-lg border border-dashed border-line-strong', compact ? 'px-3 py-2.5' : 'px-4 py-3')}>
      <p className="flex items-center gap-2 text-[13px] font-medium text-ink-2"><CircleSlash className="size-3.5 text-ink-3" />Not visible here yet</p>
      <p className="mt-1 text-[12.5px] leading-relaxed text-ink-3">
        {missingSources.slice(0, compact ? 3 : 5).map(s => s.short).join(', ').replace(/^./, c => c.toUpperCase())} aren’t connected yet.
        {' '}This shows inventory only.
      </p>
    </div>
  );
}

export function RecordPreview({ hit, onClose }: { hit: SearchHit; onClose: () => void }) {
  const { mspId, remember } = useScope();
  const now = useNow();
  const summary = deviceSummary(hit, now);
  const question = `Investigate ${hit.title}`;
  return (
    <aside aria-label={`Preview of ${hit.title}`} className="sticky top-20 animate-rise rounded-xl border border-line bg-panel shadow-card">
      <div className="flex items-start gap-3 border-b border-line px-4 py-3.5">
        <div className="min-w-0 flex-1">
          <p className="text-[12px] font-medium text-ink-3">Preview</p>
          <h2 className="truncate font-mono text-[15px] font-semibold">{hit.title}</h2>
        </div>
        <button type="button" onClick={onClose} aria-label="Close preview" className="rounded-md p-1 text-ink-3 hover:bg-hover hover:text-ink"><X className="size-4" /></button>
      </div>
      <div className="px-4 py-3">
        <p className={cn('text-[13.5px] leading-relaxed', summary.tone === 'warn' ? 'text-ink' : 'text-ink-2')}>{summary.text}</p>
        <div className="mt-2"><DeviceFacts hit={hit} /></div>
        <div className="mt-3 flex gap-2">
          <Link to={recordPath(mspId, hit.kind, hit.id)} onClick={() => remember(hit)} className={buttonClass('primary', 'md', 'flex-1 justify-center')}>
            Open device <ArrowUpRight className="size-4" />
          </Link>
          <Link to={`/w/${mspId}/ask?${new URLSearchParams({ q: question, device: hit.id })}`} className={buttonClass('secondary', 'md')}>
            <Sparkles className="size-4 text-brand" /> Investigate
          </Link>
        </div>
        <div className="mt-3"><NotVisible compact /></div>
      </div>
    </aside>
  );
}
